/**
 * Funciones genéricas y reutilizables para ordenar listas
 * Centraliza toda la lógica de ordenamiento para evitar duplicación
 */

/**
 * Crea un string comparable a partir de una fecha parcial
 * Formatea como "YYYY-MM-DD" con 999 para campos nulos
 */
const crearComparableFecha = (anio, mes, dia) => {
  const a = String((anio ?? 0) || 0).padStart(4, '0');
  const m = String((mes ?? 999) || 999).padStart(3, '0');
  const d = String((dia ?? 999) || 999).padStart(3, '0');
  return `${a}-${m}-${d}`;
};

/**
 * Obtiene propietarios de un estadio como string
 */
const getPropietariosEstadio = (estadio) => {
  if (!estadio || !estadio.clubes || estadio.clubes.length === 0) {
    return '';
  }
  return estadio.clubes.map((c) => c.nombre).join(', ');
};

/**
 * Configura los campos ordenables para un tipo de lista
 */
export const CamposOrdenamiento = {
  // Personas
  APELLIDO: 'apellido',
  NOMBRE: 'nombre',
  PAIS: 'pais',
  NACIMIENTO: 'nacimiento',

  // Clubes
  LOCALIDAD: 'localidad',
  FUNDACION: 'fundacion',

  // Estadios
  INAUGURACION: 'inauguracion',
  PROPIETARIO: 'propietario',

  // Enfrentamientos
  RIVAL: 'rival',
  PARTIDOS: 'partidos',
  GANADOS: 'ganados',
  EMPATADOS: 'empatados',
  PERDIDOS: 'perdidos',
  GOLES_FAVOR: 'golesFavor',
  GOLES_CONTRA: 'golesContra',
};

/**
 * Ordena una lista genérica según el campo y dirección
 * Adaptable a diferentes tipos de objetos
 *
 * @param {Array} lista - lista a ordenar
 * @param {string} campo - campo por el que ordenar
 * @param {boolean} ascendente - dirección del ordenamiento
 * @param {object} config - configuración con getters para campos específicos
 * @returns {Array} - nueva lista ordenada
 */
export const ordenarLista = (lista, campo, ascendente = true, config = {}) => {
  if (!lista || lista.length === 0) return lista;

  const listaOrdenada = [...lista];

  listaOrdenada.sort((a, b) => {
    let comparacion = 0;

    // Usar getters personalizados del config si existen
    if (config[campo]) {
      const valueA = config[campo](a);
      const valueB = config[campo](b);

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        comparacion = valueA.localeCompare(valueB, 'es');
      } else if (typeof valueA === 'number' && typeof valueB === 'number') {
        comparacion = valueA - valueB;
      } else {
        comparacion = String(valueA || '').localeCompare(String(valueB || ''), 'es');
      }
    } else {
      // Fallback: intentar ordenar por campo directo
      comparacion = (a[campo] || '').toString().localeCompare((b[campo] || '').toString(), 'es');
    }

    return ascendente ? comparacion : -comparacion;
  });

  return listaOrdenada;
};

/**
 * Configuración de campos ordenables para Personas
 */
export const configPersonas = {
  [CamposOrdenamiento.APELLIDO]: (p) => p.apellido || '',
  [CamposOrdenamiento.NOMBRE]: (p) => p.nombre || '',
  [CamposOrdenamiento.PAIS]: (p) => p.paisNacimiento?.nombre || '',
  [CamposOrdenamiento.NACIMIENTO]: (p) =>
    crearComparableFecha(p.anioNacimiento, p.mesNacimiento, p.diaNacimiento),
};

/**
 * Configuración de campos ordenables para Clubes
 */
export const configClubes = {
  [CamposOrdenamiento.NOMBRE]: (c) => c.nombre || '',
  [CamposOrdenamiento.PAIS]: (c) => c.pais?.nombre || '',
  [CamposOrdenamiento.LOCALIDAD]: (c) => c.localidad?.nombre || '',
  [CamposOrdenamiento.FUNDACION]: (c) =>
    crearComparableFecha(c.anioFundacion, c.mesFundacion, c.diaFundacion),
};

/**
 * Configuración de campos ordenables para Estadios
 */
export const configEstadios = {
  [CamposOrdenamiento.NOMBRE]: (e) => e.nombre || '',
  [CamposOrdenamiento.LOCALIDAD]: (e) => e.localidad?.nombre || '',
  [CamposOrdenamiento.INAUGURACION]: (e) => e.anioInauguracion || 0,
  [CamposOrdenamiento.PROPIETARIO]: (e) => getPropietariosEstadio(e),
};

/**
 * Configuración de campos ordenables para Enfrentamientos (Rivales)
 */
export const configEnfrentamientos = {
  [CamposOrdenamiento.RIVAL]: (r) => r.paisRival?.nombre || '',
  [CamposOrdenamiento.PARTIDOS]: (r) => r.totalPartidos || 0,
  [CamposOrdenamiento.GANADOS]: (r) => r.ganados || 0,
  [CamposOrdenamiento.EMPATADOS]: (r) => r.empatados || 0,
  [CamposOrdenamiento.PERDIDOS]: (r) => r.perdidos || 0,
  [CamposOrdenamiento.GOLES_FAVOR]: (r) => r.golesAFavor || 0,
  [CamposOrdenamiento.GOLES_CONTRA]: (r) => r.golesEnContra || 0,
};

/**
 * Funciones específicas para cada tipo de lista (por compatibilidad)
 */

export const ordenarPersonas = (lista, campo, ascendente = true) => {
  return ordenarLista(lista, campo, ascendente, configPersonas);
};

export const ordenarClubes = (lista, campo, ascendente = true) => {
  return ordenarLista(lista, campo, ascendente, configClubes);
};

export const ordenarEstadios = (lista, campo, ascendente = true) => {
  return ordenarLista(lista, campo, ascendente, configEstadios);
};

export const ordenarRivales = (lista, campo, ascendente = true) => {
  return ordenarLista(lista, campo, ascendente, configEnfrentamientos);
};
